import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import PathwayStagesTableCard from '@pathways/cards/Pathways/PathwayStagesTable';
import PathwayDetailsCard from '@pathways/cards/Pathways/PathwayDetails';
import IndexEventTriggeredRulesCard from '@pathways/cards/AppUsers/IndexEventTriggeredRules';
import CareTeamMembers from '@pathways/cards/AppUsers/CareTeamMembers';
import { Card, Column, ColumnGroup } from '@cards/Card';
import { selectAppUser } from '@redux/appUsers/reducers';
import { fetchAppUserPathwayCareTeam } from '@redux/careTeam/actions';
import { selectAppUserPathwayCareTeam } from '@redux/careTeam/reducers';
import {
  fetchAppUserJourneys,
  processAppUserPathwayNow,
  setAppUserPathwayStage,
} from '@pathways/redux/appUserPathways/actions';
import {
  selectPathwaysForAppUserWithOriginals,
  selectJourneysForAppUser,
  selectAppUserPathwayRuleTriggering,
} from '@pathways/redux/appUserPathways/reducers';
import { triggerAdhocRule } from '../../redux/appUserPathways/actions';
import { matchType } from '../../../propTypes';
import { setCurrentScreen, screens } from '../../../analytics';

function PathwayDetails({ match }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    params: { id: appUserId, pathwayId },
  } = match;

  useEffect(() => {
    setCurrentScreen(screens.APP_USER_PATHWAY);
  }, []);

  useEffect(() => {
    dispatch(fetchAppUserJourneys(appUserId));
    dispatch(fetchAppUserPathwayCareTeam(appUserId, pathwayId));
  }, [appUserId, pathwayId, dispatch]);

  const [appUserLoading, appUser] = useSelector(selectAppUser(appUserId));
  const [journeysLoading, journeys] = useSelector(selectJourneysForAppUser(appUser));
  const [loading, appUserPathways] = useSelector(selectPathwaysForAppUserWithOriginals(appUser));
  const ruleTriggering = useSelector(selectAppUserPathwayRuleTriggering);
  const [, careTeamRoles] = useSelector(selectAppUserPathwayCareTeam(appUserId, pathwayId));

  const appUserPathway = useMemo(
    () => (appUserPathways || []).find(({ id }) => id === Number(pathwayId)),
    [appUserPathways, pathwayId],
  );

  const appUserPathwayCareTeam = useMemo(() => {
    if (!careTeamRoles || !appUserPathway?.originalPathway?.roles) {
      return [];
    }

    return careTeamRoles.map(role => {
      const pathwayRole = appUserPathway.originalPathway.roles.find(r => r.name === role.name);

      return {
        ...role,
        permissions: pathwayRole?.permissions || [],
      };
    });
  }, [careTeamRoles, appUserPathway]);

  const roleRulesMap = useMemo(() => {
    return appUserPathway?.originalPathway?.roles?.reduce((acc, role) => {
      role.permissions.forEach(permission => {
        const ruleId = permission.id || permission.rule?.id;
        if (!ruleId) {
          return;
        }

        if (!acc[ruleId]) {
          acc[ruleId] = [];
        }

        acc[ruleId].push({
          roleName: role.name,
          permissionType: permission.permissionType,
        });
      });
      return acc;
    }, {});
  }, [appUserPathway]);

  const triggerRule = useCallback(
    ruleId => {
      dispatch(triggerAdhocRule(appUserId, pathwayId, ruleId));
    },
    [dispatch, appUserId, pathwayId],
  );

  const journey = journeys ? journeys[0] : {};

  const triggeredRuleIds = useMemo(
    () =>
      (journey?.entries || []).reduce(
        (ruleIds, entry) =>
          entry.type === 'rule_execution'
            ? [
                ...ruleIds,
                {
                  id: entry.data.original_rule_id,
                  stageSlug: entry.data.execution_details?.current_stage_slug,
                },
              ]
            : ruleIds,
        [],
      ),
    [journey],
  );

  const transitionPathwayToStage = useCallback(
    stageSlug => dispatch(setAppUserPathwayStage(appUserId, stageSlug, appUserPathway?.id)),
    [dispatch, appUserId, appUserPathway],
  );

  return (
    <>
      <Column>
        <PathwayStagesTableCard
          currentStageSlug={appUserPathway?.currentStageSlug}
          loading={appUserLoading || journeysLoading || loading || ruleTriggering}
          pathwayId={appUserPathway?.originalPathway?.id}
          triggeredRules={triggeredRuleIds}
          transitionPathwayToStage={transitionPathwayToStage}
          triggerAdhocRule={triggerRule}
          journey={journey}
          includeDeletedStages
          appUser={appUser}
          roleRulesMap={roleRulesMap}
        />
        <IndexEventTriggeredRulesCard
          journey={journey}
          pathwayId={appUserPathway?.originalPathway?.id}
          appUser={appUser}
          roleRulesMap={roleRulesMap}
        />
        <CareTeamMembers
          appUserPathwayCareTeam={appUserPathwayCareTeam}
          appUserPathway={appUserPathway}
          appUserId={appUserId}
        />
        <ColumnGroup>
          <Column>
            <ColumnGroup>
              <Column>
                <PathwayDetailsCard pathwayId={appUserPathway?.originalPathway?.id} />
              </Column>
              <Column>
                <Card title={t('patients:Pathways.processPathway.title')}>
                  <p>{t('patients:Pathways.processPathway.info')}</p>
                  <Button
                    onClick={() => dispatch(processAppUserPathwayNow(appUserId, pathwayId))}
                    disabled={appUser?.relationship?.is_cancelled}
                  >
                    {t('common:buttons.processNow')}
                  </Button>
                </Card>
              </Column>
            </ColumnGroup>
          </Column>
        </ColumnGroup>
      </Column>
    </>
  );
}

PathwayDetails.propTypes = {
  match: matchType.isRequired,
};

export default PathwayDetails;
